import React, { useState } from "react";
import {
  View,
  Text,
  Modal,
  TextInput,
  Pressable,
  StyleSheet,
  Platform,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";

type AddCategoryModalProps = {
  visible: boolean;
  onClose: () => void;
  onSave: (categoryName: string) => void;
  existingNames?: string[];
  title?: string;
  saveButtonText?: string;
};

const AddCategoryModal: React.FC<AddCategoryModalProps> = ({
  visible,
  onClose,
  onSave,
  existingNames = [],
  title = "Add Category",
  saveButtonText = "Add Category",
}) => {
  const [categoryName, setCategoryName] = useState("");
  const isDuplicate = Boolean(categoryName.trim()) && existingNames.some(
    (name) => name.trim().toLowerCase() === categoryName.trim().toLowerCase()
  );

  const handleSave = () => {
    if (categoryName.trim()) {
      onSave(categoryName);
      setCategoryName("");
    }
  };

  const handleClose = () => {
    setCategoryName("");
    onClose();
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
    >
      <View style={styles.overlay}>
        <Pressable style={styles.overlay} onPress={handleClose} />
        <View style={styles.modal}>
          <View style={styles.header}>
            <Text style={styles.title}>{title}</Text>
            <Pressable onPress={handleClose} style={styles.closeButton}>
              <MaterialIcons name="close" size={24} color="#034895" />
            </Pressable>
          </View>

          <View style={styles.body}>
            <View style={styles.field}>
              <Text style={styles.label}>
                Category Name <Text style={styles.required}>*</Text>
              </Text>
              <TextInput
                style={[styles.input, isDuplicate && styles.inputError]}
                placeholder="e.g. Facilities Request"
                value={categoryName}
                onChangeText={setCategoryName}
                maxLength={60}
                placeholderTextColor="#999"
              />
              {isDuplicate ? <Text style={styles.errorText}>Duplicate Category</Text> : null}
              <Text style={styles.helpText}>
                Shown on the Submit Ticket form and the Team Assignment tab.
              </Text>
            </View>
          </View>

          <View style={styles.footer}>
            <Pressable
              style={[styles.button, styles.cancelButton]}
              onPress={handleClose}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </Pressable>
            <Pressable
              style={[
                styles.button,
                styles.saveButton,
                (!categoryName.trim() || isDuplicate) && styles.saveButtonDisabled,
              ]}
              onPress={handleSave}
              disabled={!categoryName.trim() || isDuplicate}
            >
              <Text style={styles.saveButtonText}>{saveButtonText}</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(20, 30, 50, 0.45)",
    justifyContent: "center",
    alignItems: "center",
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: 12,
    minWidth: 480,
    maxWidth: 640,
    ...Platform.select({
      web: {
        maxHeight: "85vh",
        boxShadow: "0 8px 24px rgba(20, 30, 50, 0.16)",
      } as any,
      default: {
        shadowColor: "#141E32",
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.16,
        shadowRadius: 24,
        elevation: 8,
      },
    }),
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 24,
    paddingVertical: 18,
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  title: {
    fontSize: 18,
    fontWeight: "700",
    color: "#2D2D2D",
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  body: {
    paddingHorizontal: 24,
    paddingVertical: 20,
  },
  field: {
    marginBottom: 16,
  },
  label: {
    fontSize: 12,
    fontWeight: "600",
    color: "#585858",
    marginBottom: 6,
  },
  required: {
    color: "#D32F2F",
  },
  input: {
    width: "100%",
    height: 40,
    borderWidth: 1,
    borderColor: "#D9D9D9",
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    color: "#2D2D2D",
    marginBottom: 6,
  },
  inputError: {
    borderColor: "#D32F2F",
  },
  errorText: {
    color: "#D32F2F",
    fontSize: 12,
    marginBottom: 6,
  },
  helpText: {
    fontSize: 11,
    color: "#818181",
    marginTop: 6,
    lineHeight: 15,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 8,
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderTopWidth: 1,
    borderTopColor: "#F0F0F0",
  },
  button: {
    paddingHorizontal: 24,
    paddingVertical: 8,
    borderRadius: 100,
    minWidth: 0,
    justifyContent: "center",
    alignItems: "center",
  },
  cancelButton: {
    backgroundColor: "transparent",
  },
  cancelButtonText: {
    color: "#585858",
    fontSize: 14,
    fontWeight: "700",
  },
  saveButton: {
    backgroundColor: "#034895",
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    color: "#fff",
    fontSize: 14,
    fontWeight: "700",
  },
});

export default AddCategoryModal;
